/**
 * StructureQuickActions — Quick buttons for common structure operations
 * One of the 3 input methods (alongside drag & drop and PULSE chat)
 * All operations produce the same sections array format
 */
import React, { useState } from 'react';
import { Zap, Minus, Plus, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import { SECTION_TYPES, recalcSections } from './StructureTimeline';

const QUICK_ACTIONS = [
  { id: 'double_drop', label: 'Doble drop', icon: Zap, color: '#FF6B00',
    desc: 'Duplica el drop para mas impacto' },
  { id: 'more_intro', label: '+ Intro', icon: Plus, color: '#3F3F46',
    desc: 'Alarga la intro para build-up' },
  { id: 'remove_verse', label: '- Verso', icon: Minus, color: '#7C3AED',
    desc: 'Quita un verso, mas directo' },
  { id: 'add_bridge', label: '+ Puente', icon: Plus, color: '#0891B2',
    desc: 'Breakdown antes del final' },
  { id: 'make_shorter', label: 'Mas directo', icon: Minimize2, color: '#F5C842',
    desc: 'Quita intro larga y breakdowns' },
  { id: 'make_epic', label: 'Mas epico', icon: Maximize2, color: '#7C3AED',
    desc: 'Intro + build + doble drop' },
  { id: 'reset', label: 'Reset', icon: RotateCcw, color: '#52525B',
    desc: 'Volver a la estructura original' },
];

export function applyQuickAction(actionId, sections, bpm, defaultSections) {
  let newSections = [...sections];

  switch (actionId) {
    case 'double_drop': {
      const dropIdx = newSections.findIndex(s => s.type === 'drop');
      if (dropIdx >= 0) {
        const drop = { ...newSections[dropIdx] };
        newSections.splice(dropIdx + 1, 0, drop);
      }
      break;
    }
    case 'more_intro': {
      const introIdx = newSections.findIndex(s => s.type === 'intro');
      if (introIdx >= 0) {
        newSections[introIdx] = { ...newSections[introIdx], bars: newSections[introIdx].bars + 8 };
      } else {
        newSections.unshift({ type: 'intro', bars: 8, startTime: 0, endTime: 0 });
      }
      break;
    }
    case 'remove_verse': {
      const verseIdx = newSections.findIndex(s => s.type === 'verse');
      if (verseIdx >= 0 && newSections.length > 2) {
        newSections.splice(verseIdx, 1);
      }
      break;
    }
    case 'add_bridge': {
      const outroIdx = newSections.findIndex(s => s.type === 'outro');
      const insertAt = outroIdx > 0 ? outroIdx : newSections.length - 1;
      newSections.splice(insertAt, 0, { type: 'breakdown', bars: 8, startTime: 0, endTime: 0 });
      break;
    }
    case 'make_shorter': {
      // Shorten intro, remove breakdowns
      newSections = newSections.filter(s => s.type !== 'breakdown');
      const intro = newSections.find(s => s.type === 'intro');
      if (intro && intro.bars > 4) intro.bars = 4;
      // Remove duplicate verses
      let seenVerse = false;
      newSections = newSections.filter(s => {
        if (s.type === 'verse') {
          if (seenVerse) return false;
          seenVerse = true;
        }
        return true;
      });
      break;
    }
    case 'make_epic': {
      // Ensure: intro(16) + build(8) + drop + breakdown(8) + drop + outro
      newSections = [];
      newSections.push({ type: 'intro', bars: 16, startTime: 0, endTime: 0 });
      newSections.push({ type: 'verse', bars: 16, startTime: 0, endTime: 0 });
      newSections.push({ type: 'prechorus', bars: 8, startTime: 0, endTime: 0 });
      newSections.push({ type: 'drop', bars: 16, startTime: 0, endTime: 0 });
      newSections.push({ type: 'breakdown', bars: 8, startTime: 0, endTime: 0 });
      newSections.push({ type: 'prechorus', bars: 8, startTime: 0, endTime: 0 });
      newSections.push({ type: 'drop', bars: 16, startTime: 0, endTime: 0 });
      newSections.push({ type: 'outro', bars: 8, startTime: 0, endTime: 0 });
      break;
    }
    case 'reset': {
      return defaultSections ? [...defaultSections] : sections;
    }
    default:
      return sections;
  }

  if (newSections.length < 2) return sections;
  return recalcSections(newSections, bpm);
}

export default function StructureQuickActions({ sections, bpm, defaultSections, onApply, onChat }) {
  const [lastAction, setLastAction] = useState(null);

  const handleAction = (action) => {
    const result = applyQuickAction(action.id, sections, bpm, defaultSections);
    onApply?.(result);
    setLastAction(action.id);
    // Also notify chat so PULSE acknowledges the change
    if (onChat && action.id !== 'reset') {
      const chatDesc = {
        double_drop: 'He duplicado el drop',
        more_intro: 'He alargado la intro',
        remove_verse: 'He quitado un verso',
        add_bridge: 'He anadido un puente',
        make_shorter: 'He hecho la estructura mas directa',
        make_epic: 'He cambiado a estructura epica',
      };
      onChat(chatDesc[action.id] || 'He modificado la estructura');
    }
    setTimeout(() => setLastAction(null), 1500);
  };

  return (
    <div className="flex flex-wrap gap-1" data-testid="structure-quick-actions">
      {QUICK_ACTIONS.map((action) => {
        const Icon = action.icon;
        const isLast = lastAction === action.id;
        return (
          <button
            key={action.id}
            onClick={() => handleAction(action)}
            data-testid={`quick-action-${action.id}`}
            title={action.desc}
            className={`
              flex items-center gap-1 px-2 py-1 rounded-md text-[8px] sm:text-[9px] font-medium
              border transition-all
              ${isLast
                ? 'border-[#F5C842]/60 bg-[#F5C84220] text-[#F5C842]'
                : 'border-[#27272A] bg-[#0F0F12] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white'
              }
            `}
          >
            <Icon size={9} style={{ color: action.color }} />
            {action.label}
          </button>
        );
      })}
    </div>
  );
}
